import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { FormProvider, useForm } from "react-hook-form";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import RHFInput from "@/components/form/RHFInput";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import AuthService from "@/services/authService";
import { forgotPasswordSchema } from "./validation";
import { useBranding } from "@/hooks/useBranding";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const currentTheme = useSelector((state) => state.theme.theme);
  const isDark = currentTheme === "dark";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { branding } = useBranding();

  const methods = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onSubmit",
    defaultValues: {
      email: "",
    },
  });

  const onHandleSubmit = methods.handleSubmit(async (data) => {
    try {
      setIsSubmitting(true);

      const response = await AuthService.forgotPassword({
        email: data.email,
      });

      toast.success(response?.message || "If an account with that email exists, a reset link has been sent.");

      methods.reset();
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Unable to process your request");
    } finally {
      setIsSubmitting(false);
    }
  });

  const brandDisplayName = branding?.displayName;

  useEffect(() => {
    const suffix = brandDisplayName || "IMS People Possible AI";
    document.title = `Forgot Password | ${suffix}`;
  }, [brandDisplayName]);

  return (
    <div className="fixed inset-0 overflow-hidden">
      <div
        className={cn(
          "h-full w-full flex items-center justify-center bg-cover bg-center bg-no-repeat px-4",
          isDark ? "bg-[url('/images/home_page_bg_dark.jpg')]" : "bg-[url('/images/home_page_bg_light.jpg')]",
        )}
      >
        {/* dark overlay */}
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm pointer-events-none" aria-hidden />

        {/* modal wrapper */}
        <div className="relative z-10 w-full max-w-[420px] sm:max-w-[460px]">
          <Card
            className={cn(
              "mx-auto flex flex-col rounded-2xl border border-white/25 bg-white/10",
              "backdrop-blur-2xl shadow-[0_20px_80px_rgba(0,0,0,0.45)]",
              "px-4 py-4 sm:px-6 sm:py-6",
              "max-h-[92vh] overflow-hidden",
            )}
          >
            <header className="mb-2 text-center">
              {branding?.logoUrl && (
                <div className="flex justify-center mb-3">
                  <img src={branding.logoUrl} alt={brandDisplayName || "Brand logo"} className="h-10 w-auto object-contain" />
                </div>
              )}
              <div className="inline-flex items-center rounded-full border border-white/25 bg-black/10 px-3 py-[2px] text-[10px] sm:text-[11px] font-light tracking-wide text-text-base/80">
                Password recovery
              </div>
              <h1 className="mt-2 text-lg sm:text-xl font-medium text-text-base">
                {brandDisplayName ? `Forgot your ${brandDisplayName} password?` : "Forgot your password?"}
              </h1>
              <p className="mt-1 text-[11px] sm:text-xs text-text-base/70">Enter your email and we'll send you a reset link.</p>
            </header>

            <FormProvider {...methods}>
              <form onSubmit={onHandleSubmit} className="space-y-3 pt-1 text-sm">
                <RHFInput name="email" label="Email" placeholder="you@company.com" type="email" />

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-1 w-full rounded-lg
                             bg-gradient-to-r from-indigo-500 to-blue-600
                             hover:from-indigo-600 hover:to-blue-700
                             text-text-base flex items-center justify-center gap-2
                             py-2.5 text-sm transition-all duration-200
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      Send reset link <ArrowRight size={16} />
                    </>
                  )}
                </Button>
              </form>
            </FormProvider>

            <div className="mt-3 text-center text-[11px] sm:text-xs text-text-base/70">
              <Link to="/login" className="text-indigo-200 hover:text-indigo-100 hover:underline">
                Back to sign in
              </Link>
            </div>

            <div className="mt-2 text-center text-[10px] sm:text-[11px] text-text-base/60">
              <Link to="/" className="inline-flex items-center gap-1.5 text-indigo-200 hover:text-indigo-100 hover:underline group">
                <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
                Back to home
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
